import { useState, useCallback, useEffect } from 'react'

interface UseProgressLoaderOptions {
  /**
   * The maximum progress value (default: 90)
   */
  maxProgress?: number
  /**
   * The interval in milliseconds between progress updates (default: 200)
   */
  intervalMs?: number
  /**
   * The amount to increment the progress by on each interval (default: 7)
   */
  incrementBy?: number
  /**
   * Callback function to be called when progress reaches the maxProgress
   */
  onMaxProgress?: () => void
}

/**
 * A custom hook that manages a progress value that increments over time
 * until it reaches a maximum value.
 *
 * @param options Configuration options for the progress loader
 * @returns An object containing the current progress and functions to control the loader
 */
export const useProgressLoader = (options: UseProgressLoaderOptions = {}) => {
  const { maxProgress = 90, intervalMs = 200, incrementBy = 7, onMaxProgress } = options

  const [progress, setProgress] = useState(0)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)

  const startLoader = useCallback(() => {
    // Clear any existing interval
    if (intervalId) {
      clearInterval(intervalId)
    }

    setProgress(0)

    const id = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + incrementBy
        if (newProgress >= maxProgress) {
          clearInterval(id)
          onMaxProgress?.()
          return maxProgress
        }
        return newProgress
      })
    }, intervalMs)

    setIntervalId(id)

    return () => {
      clearInterval(id)
    }
  }, [intervalId, incrementBy, intervalMs, maxProgress, onMaxProgress])

  const complete = useCallback(
    (finalProgress = 100) => {
      if (intervalId) {
        clearInterval(intervalId)
        setIntervalId(null)
      }
      setProgress(finalProgress)
    },
    [intervalId],
  )

  const reset = useCallback(() => {
    if (intervalId) {
      clearInterval(intervalId)
      setIntervalId(null)
    }
    setProgress(0)
  }, [intervalId])

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [intervalId])

  return {
    progress,
    startLoader,
    complete,
    reset,
    isComplete: progress >= 100,
  }
}

export default useProgressLoader
